import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { VariableSizeGrid as Grid } from "react-window";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  PermissionMatrixCell,
  PermissionMatrixRow,
  PermissionMatrixFilter,
  PermissionAction,
  ResourceType,
  SystemRole,
  ProjectRole,
  PermissionSource,
} from "@/types/permissions";
import { usePermissionMatrix } from "@/hooks/permissions/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Download,
  Upload,
  Search,
  Filter,
  RotateCcw,
  Check,
  X,
  Minus,
  AlertCircle,
  Shield,
  Lock,
  Unlock,
  Eye,
  Edit,
  Trash,
  Share,
  FileDown,
  FileUp,
  Archive,
  Settings,
  Users,
  BarChart,
  Command,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Permission action icons
const ACTION_ICONS: Record<
  PermissionAction,
  React.FC<{ className?: string }>
> = {
  [PermissionAction.CREATE]: FileUp,
  [PermissionAction.READ]: Eye,
  [PermissionAction.UPDATE]: Edit,
  [PermissionAction.DELETE]: Trash,
  [PermissionAction.SHARE]: Share,
  [PermissionAction.EXPORT]: FileDown,
  [PermissionAction.IMPORT]: Upload,
  [PermissionAction.PUBLISH]: Command,
  [PermissionAction.ARCHIVE]: Archive,
  [PermissionAction.RESTORE]: RotateCcw,
  [PermissionAction.MANAGE_PERMISSIONS]: Shield,
  [PermissionAction.MANAGE_MEMBERS]: Users,
  [PermissionAction.MANAGE_SETTINGS]: Settings,
  [PermissionAction.BULK_OPERATIONS]: Command,
  [PermissionAction.EXECUTE_WORKFLOW]: Command,
  [PermissionAction.VIEW_ANALYTICS]: BarChart,
};

interface PermissionMatrixProps {
  projectId?: string;
  organizationId?: string;
  onPermissionChange?: (cell: PermissionMatrixCell) => void;
  readOnly?: boolean;
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  projectId,
  organizationId,
  onPermissionChange,
  readOnly = false,
}) => {
  const { fetchMatrix, bulkUpdate, isBulkUpdating, exportMatrix } =
    usePermissionMatrix();

  // State
  const [matrixData, setMatrixData] = useState<PermissionMatrixRow[]>([]);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<PermissionMatrixFilter>({
    showInherited: true,
    showOverrides: true,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const [conflictCells, setConflictCells] = useState<Set<string>>(new Set());

  // Grid refs
  const gridRef = useRef<Grid>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Constants
  const CELL_WIDTH = 50;
  const CELL_HEIGHT = 40;
  const HEADER_HEIGHT = 80;
  const ROW_HEADER_WIDTH = 200;

  // Available actions and resources
  const actions = Object.values(PermissionAction);
  const resources = Object.values(ResourceType);

  // Load matrix data
  useEffect(() => {
    const loadMatrix = async () => {
      setIsLoading(true);
      try {
        const data = await fetchMatrix({
          ...filter,
          roles: filter.roles,
          resources: filter.resources,
          actions: filter.actions,
        });
        setMatrixData(data.rows);

        // Detect conflicts
        const conflicts = new Set<string>();
        data.rows.forEach((row: PermissionMatrixRow) => {
          row.cells.forEach((cell: PermissionMatrixCell) => {
            if (
              cell.inherited &&
              cell.allowed !==
                row.cells.find(
                  (c) =>
                    c.resourceType === cell.resourceType &&
                    c.action === cell.action &&
                    !c.inherited,
                )?.allowed
            ) {
              conflicts.add(
                getCellKey(row.roleId, cell.resourceType, cell.action),
              );
            }
          });
        });
        setConflictCells(conflicts);
      } catch (error) {
        console.error("Failed to load permission matrix:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMatrix();
  }, [fetchMatrix, filter, projectId, organizationId]);

  // Generate cell key for selection tracking
  const getCellKey = (
    roleId: string,
    resource: ResourceType,
    action: PermissionAction,
  ) => {
    return `${roleId}:${resource}:${action}`;
  };

  // Handle cell click
  const handleCellClick = useCallback(
    (
      roleId: string,
      resource: ResourceType,
      action: PermissionAction,
      currentValue: boolean,
      isInherited: boolean,
    ) => {
      if (readOnly || isInherited) return;

      const cellKey = getCellKey(roleId, resource, action);

      // Toggle selection for bulk operations
      if (selectedCells.has(cellKey)) {
        setSelectedCells((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      } else {
        setSelectedCells((prev) => new Set(prev).add(cellKey));
      }

      // Update single permission
      const cell: PermissionMatrixCell = {
        roleId,
        resourceType: resource,
        resourceId: projectId || organizationId || "",
        action,
        allowed: !currentValue,
        inherited: false,
        overridable: true,
      };

      if (onPermissionChange) {
        onPermissionChange(cell);
      }

      // Update local state optimistically
      setMatrixData((prev) =>
        prev.map((row) => {
          if (row.roleId === roleId) {
            return {
              ...row,
              cells: row.cells.map((c) => {
                if (c.resourceType === resource && c.action === action) {
                  return { ...c, allowed: !currentValue };
                }
                return c;
              }),
            };
          }
          return row;
        }),
      );
    },
    [readOnly, selectedCells, projectId, organizationId, onPermissionChange],
  );

  // Bulk operations
  const handleBulkOperation = useCallback(
    async (operation: "grant" | "revoke" | "reset") => {
      if (selectedCells.size === 0) return;

      const permissions = Array.from(selectedCells).map((key) => {
        const [roleId, resource, action] = key.split(":");
        return {
          roleId,
          resourceType: resource as ResourceType,
          action: action as PermissionAction,
        };
      });

      // Group by role for efficient bulk update
      const roleGroups = permissions.reduce(
        (acc, perm) => {
          if (!acc[perm.roleId]) {
            acc[perm.roleId] = [];
          }
          acc[perm.roleId].push({
            resourceType: perm.resourceType,
            resourceId: projectId || organizationId,
            actions: [perm.action],
          });
          return acc;
        },
        {} as Record<string, any[]>,
      );

      await bulkUpdate({
        type: operation,
        roleIds: Object.keys(roleGroups),
        permissions: Object.values(roleGroups).flat(),
      });

      setSelectedCells(new Set());
    },
    [selectedCells, bulkUpdate, projectId, organizationId],
  );

  // Export functionality
  const handleExport = useCallback(
    async (format: "csv" | "excel" | "json") => {
      await exportMatrix(format);
    },
    [exportMatrix],
  );

  // Filter handling
  const handleFilterChange = useCallback(
    (key: keyof PermissionMatrixFilter, value: any) => {
      setFilter((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Search filtering
  const filteredData = useMemo(() => {
    if (!searchQuery) return matrixData;

    return matrixData.filter((row) =>
      row.roleName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [matrixData, searchQuery]);

  // Cell renderer
  const CellRenderer = ({
    roleId,
    roleName,
    resource,
    action,
    cell,
    isHeader = false,
  }: {
    roleId: string;
    roleName: string;
    resource: ResourceType;
    action: PermissionAction;
    cell?: PermissionMatrixCell;
    isHeader?: boolean;
  }) => {
    if (isHeader) {
      const Icon = ACTION_ICONS[action];
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center h-full w-full border-b border-r p-1">
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-semibold">{action}</p>
              <p className="text-xs text-muted-foreground">{resource}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    const cellKey = getCellKey(roleId, resource, action);
    const isSelected = selectedCells.has(cellKey);
    const isConflict = conflictCells.has(cellKey);
    const isHovered = hoveredCell === cellKey;

    const cellValue =
      cell ||
      matrixData
        .find((row) => row.roleId === roleId)
        ?.cells.find((c) => c.resourceType === resource && c.action === action);

    if (!cellValue) return null;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={cn(
                "flex items-center justify-center h-full w-full border-b border-r transition-all",
                "hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-primary",
                isSelected && "bg-primary/20 ring-2 ring-primary",
                isConflict && "bg-destructive/10",
                isHovered && "bg-accent",
                cellValue.inherited && "opacity-60",
                !cellValue.allowed && "bg-muted/30",
                readOnly && "cursor-not-allowed",
              )}
              onClick={() =>
                handleCellClick(
                  roleId,
                  resource,
                  action,
                  cellValue.allowed,
                  cellValue.inherited,
                )
              }
              onMouseEnter={() => setHoveredCell(cellKey)}
              onMouseLeave={() => setHoveredCell(null)}
              disabled={readOnly || cellValue.inherited}
            >
              {cellValue.allowed ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : cellValue.allowed === false ? (
                <X className="h-4 w-4 text-red-600" />
              ) : (
                <Minus className="h-4 w-4 text-muted-foreground" />
              )}
              {cellValue.inherited && (
                <Lock className="h-3 w-3 absolute top-0 right-0 text-muted-foreground" />
              )}
              {isConflict && (
                <AlertCircle className="h-3 w-3 absolute bottom-0 right-0 text-destructive" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              <p className="font-semibold">{roleName}</p>
              <p className="text-xs">
                {action} on {resource}
              </p>
              {cellValue.inherited && (
                <p className="text-xs text-muted-foreground">
                  Inherited from {cellValue.source || "parent"}
                </p>
              )}
              {cellValue.conditions && cellValue.conditions.length > 0 && (
                <p className="text-xs text-warning">Has conditions</p>
              )}
              {isConflict && (
                <p className="text-xs text-destructive">Conflict detected</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-64"
            />
          </div>

          <Select
            value={filter.showInherited ? "show" : "hide"}
            onValueChange={(value) =>
              handleFilterChange("showInherited", value === "show")
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Inherited" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="show">Show Inherited</SelectItem>
              <SelectItem value="hide">Hide Inherited</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" onClick={() => setFilter({})}>
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {selectedCells.size > 0 && (
            <>
              <Badge variant="secondary">{selectedCells.size} selected</Badge>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleBulkOperation("grant")}
                disabled={isBulkUpdating}
              >
                <Unlock className="h-4 w-4 mr-1" />
                Grant
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleBulkOperation("revoke")}
                disabled={isBulkUpdating}
              >
                <Lock className="h-4 w-4 mr-1" />
                Revoke
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkOperation("reset")}
                disabled={isBulkUpdating}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset
              </Button>
            </>
          )}

          <div className="border-l pl-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("csv")}
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("excel")}
              className="ml-1"
            >
              <Download className="h-4 w-4 mr-1" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("json")}
              className="ml-1"
            >
              <Download className="h-4 w-4 mr-1" />
              JSON
            </Button>
          </div>
        </div>
      </div>

      {/* Matrix Grid */}
      <div ref={containerRef} className="flex-1 relative overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">
                Loading permission matrix...
              </p>
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* Fixed Headers */}
            <div className="sticky top-0 z-20 bg-background border-b">
              <div className="flex">
                <div className="w-[200px] h-[80px] border-r p-2 flex items-center justify-center bg-muted/50">
                  <span className="font-semibold text-sm">
                    Roles / Permissions
                  </span>
                </div>
                {resources.map((resource) => (
                  <div key={resource} className="border-r">
                    <div className="text-xs font-medium text-center p-1 border-b bg-muted/30">
                      {resource}
                    </div>
                    <div className="flex">
                      {actions.map((action) => (
                        <div
                          key={`${resource}-${action}`}
                          className="w-[50px] h-[40px]"
                        >
                          <CellRenderer
                            roleId=""
                            roleName=""
                            resource={resource}
                            action={action}
                            isHeader
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Matrix Body */}
            <div className="relative">
              {filteredData.map((row, rowIndex) => (
                <div key={row.roleId} className="flex border-b">
                  <div className="w-[200px] sticky left-0 z-10 bg-background border-r">
                    <div className="p-2 flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {row.roleName}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {row.roleType}
                      </Badge>
                    </div>
                  </div>
                  {resources.map((resource) => (
                    <div key={`${row.roleId}-${resource}`} className="flex">
                      {actions.map((action) => {
                        const cell = row.cells.find(
                          (c) =>
                            c.resourceType === resource && c.action === action,
                        );
                        return (
                          <div
                            key={`${row.roleId}-${resource}-${action}`}
                            className="w-[50px] h-[40px]"
                          >
                            <CellRenderer
                              roleId={row.roleId}
                              roleName={row.roleName}
                              resource={resource}
                              action={action}
                              cell={cell}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between p-2 border-t bg-muted/30 text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>{filteredData.length} roles</span>
          <span>{resources.length} resources</span>
          <span>{actions.length} actions</span>
          <span>
            {filteredData.length * resources.length * actions.length} cells
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <Check className="h-3 w-3 text-green-600" />
            <span>Granted</span>
          </div>
          <div className="flex items-center gap-1">
            <X className="h-3 w-3 text-red-600" />
            <span>Denied</span>
          </div>
          <div className="flex items-center gap-1">
            <Lock className="h-3 w-3" />
            <span>Inherited</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertCircle className="h-3 w-3 text-destructive" />
            <span>Conflict</span>
          </div>
        </div>
      </div>
    </div>
  );
};
